import React, { useEffect, useRef, useState } from 'react';
import { ArrowRightIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';
import { Link } from 'react-router-dom';

import useSearchQuery from 'api/queries/useSearchQuery';
import { usePromoCodeContext } from 'contexts/PromoCodeContext';
import ProductCardV2 from 'features/shop/ProductCardV2';
import Button from 'honeydew/Button';
import Loader from 'honeydew/Loader';
import useGlobalStore from 'hooks/useGlobalStore';
import useTailwindTheme from 'hooks/useTailwindTheme';
import { sortSoldOutProductsLast } from 'utils/aislesUtils';
import { trackEvent } from 'utils/analyticsUtils';
import { disableBodyScroll, enableBodyScroll } from 'utils/globalUtils';

export function SearchAutocomplete({ isShopRoute }) {
    const tailwindTheme = useTailwindTheme();
    const { customerPromoNanoBanner: { showCustomerPromoNanoBanner } = {} } = usePromoCodeContext();
    const mobile = useMediaQuery({ maxWidth: tailwindTheme?.screens?.md });
    const { autocompleteOpen, searchQuery, setAutocompleteOpen, setCartOpen, setSearchQuery, setSearchResultsLength } =
        useGlobalStore();

    const { data: searchData, isLoading } = useSearchQuery(searchQuery);

    const [autocompleteItems, setAutocompleteItems] = useState([]);
    const hasTrackedRef = useRef(false);

    const { pathname } = window.location ?? {};
    const isNative = !!window.isNativeApp;

    const closeAutocomplete = () => {
        setAutocompleteOpen(false);
        enableBodyScroll(true);
        setSearchQuery('');
        setCartOpen(false);
    };

    const filterAutocompleteItems = (array = []) => sortSoldOutProductsLast(array).slice(0, 6);

    useEffect(() => {
        if (searchData?.items) {
            const filteredItems = filterAutocompleteItems(searchData.items);
            setAutocompleteItems(filteredItems);
            setSearchResultsLength(filteredItems.length);
        }
    }, [searchData]);

    // Track event once when autocomplete results are rendered (including no results)
    useEffect(() => {
        if (searchQuery === '') {
            hasTrackedRef.current = false;
            return;
        }

        if (autocompleteOpen && searchData && !isLoading && !hasTrackedRef.current) {
            hasTrackedRef.current = true;
            trackEvent('search bar result preview', { pathname });
        }
    }, [searchData, autocompleteOpen, searchQuery, isLoading, pathname]);

    useEffect(() => {
        if (!isShopRoute && autocompleteOpen) closeAutocomplete();
    }, [isShopRoute]);

    useEffect(() => {
        if (autocompleteOpen && searchQuery !== '') {
            disableBodyScroll(mobile);
        } else {
            enableBodyScroll(true);
        }
    }, [autocompleteOpen, searchQuery]);

    return (
        autocompleteOpen &&
        searchQuery !== '' &&
        isShopRoute && (
            <div>
                {!mobile && (
                    <div
                        role="button"
                        tabIndex={0}
                        onClick={closeAutocomplete}
                        onKeyDown={closeAutocomplete}
                        aria-label="Close Autocomplete"
                        className="fixed inset-0 z-20 h-full w-full overflow-y-auto bg-black bg-opacity-50"
                    />
                )}
                <div
                    className={clsx(
                        'fixed left-0 right-0 z-20 overflow-y-scroll bg-white px-10 pb-[100px] pt-15 md:bottom-auto md:pb-10 md:shadow-base',
                        {
                            // --- With promo banner ---
                            // Mobile web: header (100px) + banner (50px) = 150px
                            'bottom-0 top-[150px] md:top-[120px]': showCustomerPromoNanoBanner && mobile && !isNative,
                            // Desktop: centered box
                            'top-[150px] z-[1200] m-auto max-w-[700px] rounded-lg md:top-[120px]':
                                showCustomerPromoNanoBanner && !mobile,

                            // --- No promo banner ---
                            // Native app (same with or without promo banner)
                            'bottom-0 top-[56px]': isNative,
                            // Mobile web
                            'bottom-0 top-[100px]': !showCustomerPromoNanoBanner && !isNative && mobile,
                            // Desktop
                            'top-70 z-[1200] m-auto max-w-[700px] rounded-lg': !showCustomerPromoNanoBanner && !mobile,
                        }
                    )}
                >
                    {isLoading && searchQuery !== '' ? (
                        <div className="flex h-[400px] items-center justify-center">
                            <Loader width="50px" />
                        </div>
                    ) : (
                        autocompleteItems.length > 0 && (
                            <div>
                                <div className="flex justify-between align-bottom">
                                    <div>
                                        {searchData?.noResults && (
                                            <>
                                                <p className="mb-10 font-grotesk-bold uppercase text-grey">
                                                    No results for &quot;{searchQuery}&quot;
                                                </p>
                                                <p className="mb-10 font-grotesk-bold uppercase text-grey">
                                                    You may like
                                                </p>
                                            </>
                                        )}
                                    </div>
                                </div>

                                <div className="grid gap-15 md:grid-cols-2">
                                    {autocompleteItems.map((item) => (
                                        <ProductCardV2
                                            key={item.id}
                                            data={item}
                                            layout="horizontal"
                                            onNavigateToPDP={closeAutocomplete}
                                        />
                                    ))}
                                </div>
                                {!mobile && (
                                    <div className="my-10">
                                        <Link
                                            to={`/shop/search?query=${searchQuery}`}
                                            className="text-body-sm float-right flex items-center gap-5 font-grotesk-bold underline"
                                            onClick={closeAutocomplete}
                                        >
                                            View All <ArrowRightIcon className="mt-[1px] inline-block size-15" />
                                        </Link>
                                    </div>
                                )}
                            </div>
                        )
                    )}
                </div>
                {mobile && (
                    <div className="fixed bottom-0 left-0 right-0 z-[2000] bg-white p-20">
                        <Button size="md" as="a" to={`/shop/search?query=${searchQuery}`}>
                            View All
                        </Button>
                    </div>
                )}
            </div>
        )
    );
}

SearchAutocomplete.propTypes = {
    isShopRoute: PropTypes.bool.isRequired,
};
